import { Text } from '$tablet-app/src/app/components/Text/Text';
import { TextInput } from '$tablet-app/src/app/components/TextInput/TextInput';
import { LocalImage } from '$tablet-app/src/app/images/types';
import { useCurrentUser } from '$tablet-app/src/app/users';
import { Button } from '@akin-home/buttons/native';
import { postImageToCloud } from '@akin-home/images/frontend';
import { showErrorToast, showSuccessToast } from '@akin-home/toast/native';
import { useUpdateUser } from '@akin-home/users/react';
import { Asset } from 'expo-asset';
import React, { useState } from 'react';
import { StyleSheet } from 'react-native';
import ColorPalette from 'react-native-color-palette';
import styled from 'styled-components/native';
import { ProfileAvatars } from '../../../../../assets/profilePictures';
import { KeyboardAvoid } from '../../../components/KeyboardAvoid';
import ProfilePictureSelectionView from './ProfilePictureSelectionView';
import { spacing } from '@akin-home/theme';

// get library profile pictures
const PROFILE_PIC_DATA: any[] = [];
for (const [key, value] of Object.entries(ProfileAvatars)) {
  PROFILE_PIC_DATA.push({
    id: 'id_' + key,
    uri: Asset.fromModule(value).uri,
  });
}

const ProfileScreen = () => {
  const user = useCurrentUser();
  const updateUser = useUpdateUser();

  const [profileColor, setProfileColor] = useState(
    user?.profileColor || 'blue'
  );
  const [form, setForm] = useState({ name: user.name });

  const updateProfileColor = (color: string) => {
    setProfileColor(color);

    updateUser.mutate({
      input: { id: user.id as string, profileColor: color },
    });
  };

  const onChangeProfilePicture = async (params: {
    profilePicture: string | null;
    image: LocalImage | null;
  }) => {
    try {
      if (params.image) {
        const uploadedImage = await postImageToCloud({
          habitatID: user.habitatID,
          file: params.image,
        });
        await updateUser.mutateAsync({
          input: {
            id: user.id,
            profilePicture: null,
            imageID: uploadedImage.id,
          },
        });
      } else if (params.profilePicture) {
        updateUser.mutateAsync({
          input: {
            id: user.id as string,
            imageID: null,
            profilePicture: params.profilePicture,
          },
        });
      }
    } catch (err) {
      console.error('Failure to update profile picture', err);
      showErrorToast({
        text1: 'Something went wrong',
        text2: 'Please try again in a few minutes',
      });
    }
  };

  const onPressSave = async () => {
    await updateUser.mutate({
      input: {
        id: user.id as string,
        name: form.name,
      },
    });

    showSuccessToast({ text1: 'Profile Saved' });
  };

  const onChangeForm = ({ name, value }: any) => {
    setForm({
      ...form,
      [name]: value,
    });
  };

  return (
    <KeyboardAvoid keyboardVerticalOffset={100}>
      <Container contentContainerStyle={{ flexGrow: 1 }}>
        <ProfilePictureSelectionView
          profileColor={profileColor}
          user={user}
          onChangeProfilePicture={onChangeProfilePicture}
        />

        <Text preset="bold" style={styles.colorSelectTitle}>
          Profile color
        </Text>

        <ColorPalette
          paletteStyles={styles.colorSelector}
          onChange={updateProfileColor}
          defaultColor={user?.profileColor || '#535af1'}
          colors={[
            '#535af1',
            '#14a0ff',
            '#A8C9E0',
            '#ffbf09',
            '#3e988e',
            '#e55e5a',
            '#ff7b43',
            '#f500dc',
            '#08388d',
            '#54f16e',
            '#fc3232',
          ]}
          title=""
        />

        <NameForm>
          <Text style={styles.displayNameTitle} preset="bold">
            Display Name
          </Text>

          <TextInput
            value={form.name}
            onChangeText={(value) => onChangeForm({ name: 'name', value })}
          />

          <Button size="large" onPress={onPressSave}>
            Save
          </Button>
        </NameForm>
      </Container>
    </KeyboardAvoid>
  );
};

const Container = styled.ScrollView`
  flex: 1;
  background-color: white;
`;

const NameForm = styled.View`
  padding: 16px;
  gap: 10px;
`;

const styles = StyleSheet.create({
  colorSelectTitle: {
    marginHorizontal: 25,
    marginTop: 40,
  },
  colorSelector: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    columnGap: spacing[4],
    marginHorizontal: spacing[6],
  },
  displayNameTitle: {
    marginLeft: spacing[2],
  },
});

export default ProfileScreen;
